import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import {
  Search,
  Construction,
  TrendingUp,
  Layers,
} from 'lucide-react'
import { Container } from '../layout/Container'
import { useScrollAnimation } from '../../hooks/useScrollAnimation'
import type { TimelinePhase } from '../../types'

const iconMap: Record<string, React.ElementType> = {
  diagnose: Search,
  build: Construction,
  scale: TrendingUp,
  expand: Layers,
}

export function HowWeWorkTimeline() {
  const { t } = useTranslation()
  const { ref, isInView } = useScrollAnimation()

  const phases: TimelinePhase[] = t('howWeWork.phases', { returnObjects: true }) as TimelinePhase[]

  return (
    <section id="como-trabajamos" className="py-24">
      <Container>
        <div className="mb-16 text-center">
          <span className="text-sm font-medium uppercase tracking-wider text-accent-light">
            {t('howWeWork.label')}
          </span>
          <h2 className="mt-4 text-3xl font-semibold text-text-primary md:text-4xl">
            {t('howWeWork.title')}
          </h2>
        </div>

        <motion.div
          ref={ref}
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : { opacity: 0 }}
          transition={{ duration: 0.6 }}
          className="relative"
        >
          <div className="absolute left-6 top-0 bottom-0 w-px bg-border md:left-1/2" />

          <div className="space-y-12">
            {phases.map((phase, index) => {
              const Icon = iconMap[phase.phase] || Search
              const isEven = index % 2 === 0

              return (
                <motion.div
                  key={phase.phase}
                  initial={{ opacity: 0, x: isEven ? -24 : 24 }}
                  animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: isEven ? -24 : 24 }}
                  transition={{ duration: 0.5, delay: index * 0.15 }}
                  className={`relative flex items-center gap-8 md:gap-0 ${isEven ? 'md:flex-row' : 'md:flex-row-reverse'}`}
                >
                  <div className={`flex-1 ${isEven ? 'md:pr-12 md:text-right' : 'md:pl-12'}`}>
                    <div className={`inline-flex items-center gap-2 rounded-full border border-border-subtle bg-surface px-3 py-1 text-xs font-medium text-text-secondary ${isEven ? 'md:flex-row-reverse' : ''}`}>
                      <span className="text-accent-light">{phase.phase}</span>
                      <span>{phase.duration}</span>
                    </div>
                    <h3 className="mt-3 text-xl font-semibold text-text-primary">{phase.title}</h3>
                    <p className="mt-2 text-text-secondary">{phase.description}</p>
                  </div>

                  <div className="absolute left-6 top-0 z-10 flex h-12 w-12 -translate-x-1/2 items-center justify-center rounded-full border border-border-subtle bg-surface-elevated text-accent-light md:left-1/2">
                    <Icon className="h-5 w-5" />
                  </div>

                  <div className="hidden flex-1 md:block" />
                </motion.div>
              )
            })}
          </div>
        </motion.div>
      </Container>
    </section>
  )
}
